// src/services/textService.js
import api from "./api";

/**
 * Anexa AuthorId/CategoryId ao FormData sabendo que os dois são OPCIONAIS.
 *
 * `FormData.append` converte o valor em string por conta própria: um
 * `authorId` nulo — texto sem autor indicado — vira a string "null", e o
 * ModelBinder do ASP.NET responde 400 com "The value 'null' is not valid for
 * AuthorId". Não é o backend recusando texto sem autor; é ele recebendo a
 * palavra "null" onde esperava um número.
 *
 * O 0 não é um palpite: `TextUpdateDto`/`TextCreateDto` declaram `int?` e o
 * TextsController normaliza com `dto.AuthorId > 0 ? dto.AuthorId : null`,
 * sob o comentário "0 ou ausente = sem catalogação". É também o que
 * EditTextForm e updateTextFields já enviavam — quem passava o valor cru é
 * que destoava.
 */
function appendOptionalId(formData, field, value) {
  const n = Number(value);
  formData.append(field, String(Number.isFinite(n) && n > 0 ? n : 0));
}

export function fetchTexts() {
  return api.get("/texts");
  //return api.get('/texts/search-elastic');
}

export function getTextById(id) {
  return api.get(`/texts/${id}`);
}

/**
 * Extrai o conteúdo de um arquivo que o navegador não sabe ler sozinho — hoje
 * PDF, EPUB e DOCX — e devolve.
 *
 * NÃO cria texto nenhum: o conteúdo volta para o editor, o usuário revisa, e o
 * salvamento segue pelo `createText` de sempre, como conteúdo colado. O `.txt`
 * não passa por aqui — `file.text()` no navegador resolve, sem ida ao servidor.
 *
 * `isHtml` distingue o que voltou: PDF devolve texto puro (`isHtml: false`),
 * com um parágrafo por linha em branco — o chamador ainda precisa convertê-lo
 * para HTML antes de preencher o editor. EPUB e DOCX já devolvem HTML canônico
 * (`isHtml: true`), prontos para ir direto ao editor sem reconversão — eles já
 * chegam com a própria marcação (títulos, itálico, listas), e reduzi-los a
 * texto puro destruiria essa estrutura.
 *
 * `.doc` (Word 97-2003, binário) não é suportado: a API recusa com mensagem
 * própria instruindo a salvar como `.docx`.
 *
 * @param {File} file
 * @returns {Promise<{ data: { text: string, isHtml: boolean, fileName: string, characters: number } }>}
 */
export function extractTextFromFile(file) {
  const formData = new FormData();
  formData.append("file", file);

  return api.post("/texts/extract-text", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function createText(data, useElastic = false) {
  console.log("textService - createText: data recebida", data);

  const formData = new FormData();

  // =========================================================
  // ✅ PATCH: TopicsId agora é MULTI (FormData repetido)
  // Aceita tanto data.topicIds (novo) quanto data.topicsId (legado)
  // =========================================================
  const topicIdsArr = Array.isArray(data.topicIds)
    ? data.topicIds
    : Array.isArray(data.topicsId)
    ? data.topicsId
    : [];

  topicIdsArr
    .filter((x) => x !== null && x !== undefined && `${x}`.trim() !== "")
    .forEach((id) => formData.append("TopicsId", String(id)));

  // =========================================================
  // Campos básicos
  // =========================================================
  appendOptionalId(formData, "CategoryId", data.categoryId);
  appendOptionalId(formData, "AuthorId", data.authorId);
  formData.append("Title", data.title);
  formData.append("Subtitle", data.subtitle || "");  // evita null
  formData.append("Synopsis", data.synopsis || "");
  // Capa: Blob ja recortado e redimensionado por utils/coverImage.js. O nome
  // do arquivo e irrelevante — o backend nomeia por GUID e tira a extensao do
  // content-type.
  if (data.coverBlob) formData.append("cover", data.coverBlob, "cover");
  formData.append("Source", data.source || "");
  formData.append("Active", String(!!data.active)); // garante string "true"/"false"

  // =========================================================
  // Permissões (JSON)
  // =========================================================
  formData.append("Permissions", JSON.stringify(data.permissions ?? []));
  console.log("Permissions JSON:", JSON.stringify(data.permissions ?? []));

  // =========================================================
  // Conteúdo: arquivo OU texto colado
  // =========================================================
  if (data.file) {
    // O nome 'file' DEVE corresponder ao parâmetro IFormFile? file no Controller
    formData.append("file", data.file);
  } else if (data.pastedContent) {
    // Controller recebe: [FromForm] string? pastedContentString
    formData.append("pastedContentString", data.pastedContent);

    // Diz ao backend que o conteúdo é HTML do editor rich text, e não texto puro.
    // Explícito de propósito: farejar o formato faria um texto puro que por acaso
    // contivesse "<p>" virar marcação sem ninguém pedir.
    //
    // Com a flag, o backend normaliza na entrada e o texto NASCE com
    // content_format = 'html' — sem conversão, sem ensaio e sem backup, porque um
    // texto que ainda não existe não tem nota a remapear.
    formData.append("pastedContentIsHtml", data.pastedContentIsHtml ? "true" : "false");
  }

  console.log("textService - FormData montado para envio:");
  for (let pair of formData.entries()) {
    console.log(pair[0] + ": " + pair[1]);
  }

  return api.post("/texts", formData, {
    params: { useElastic },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

// src/services/textService.js

export function updateText(data, useElastic = false) {
  console.log("textService - updateText: data recebida", data);

  const formData = new FormData();

  // =========================================================
  // Campos básicos
  // =========================================================
  formData.append("TextId", data.textId);

  // =========================================================
  // ✅ TopicsId MULTI (FormData repetido) - sem alterações aqui
  // =========================================================
  const topicIdsArr = Array.isArray(data.topicIds)
    ? data.topicIds
    : Array.isArray(data.topicsId)
    ? data.topicsId
    : [];

  topicIdsArr
    .filter((x) => x !== null && x !== undefined && `${x}`.trim() !== "")
    .forEach((id) => formData.append("TopicsId", String(id)));

  appendOptionalId(formData, "CategoryId", data.categoryId);
  appendOptionalId(formData, "AuthorId", data.authorId);
  formData.append("Title", data.title);
  formData.append("Subtitle", data.subtitle || ""); 
  formData.append("Synopsis", data.synopsis || "");
  // Capa: Blob ja recortado e redimensionado por utils/coverImage.js. O nome
  // do arquivo e irrelevante — o backend nomeia por GUID e tira a extensao do
  // content-type.
  if (data.coverBlob) formData.append("cover", data.coverBlob, "cover");
  // Só na edição: diz explicitamente "tire a capa". Ausência dos dois campos
  // significa "não mexi na capa", e é isso que impede um salvamento comum de
  // apagar a capa existente.
  if (data.removeCover) formData.append("removeCover", "true");
  formData.append("Source", data.source || "");
  formData.append("Active", String(!!data.active)); // garante string "true"/"false"
  formData.append("Permissions", JSON.stringify(data.permissions ?? []));

  // =========================================================
  // Conteúdo novo: arquivo OU texto colado
  // =========================================================
  if (data.file) {
    formData.append("file", data.file);
  } else if (data.pastedContent) {
    formData.append("pastedContentString", data.pastedContent);
    // Na edição, o backend só aceita esta flag se o texto JÁ for HTML — ele recusa
    // a combinação com texto puro, para não converter por acidente.
    formData.append(
      "pastedContentIsHtml",
      data.pastedContentIsHtml ? "true" : "false"
    );
  }

  // =========================================================
  // ✅ [PATCH SERVICE] Sempre enviar as decisões sobre notas,
  // independentemente de ser arquivo ou texto colado.
  // Antes isso só entrava no ramo "pastedContent".
  // =========================================================
  if (data.detachNoteIds && data.detachNoteIds.length > 0) {
    // Controller espera: [FromForm(Name="detachNoteIds")] string? detachNoteIdsCsv
    formData.append("detachNoteIdsCsv", data.detachNoteIds.join(","));
  }

  if (data.deleteNoteIds && data.deleteNoteIds.length > 0) {
    // Controller espera: [FromForm(Name="deleteNoteIds")] string? deleteNoteIdsCsv
    formData.append("deleteNoteIdsCsv", data.deleteNoteIds.join(","));
  }

  // Compatibilidade LEGADA
  if (data.acceptedNoteLossIds && data.acceptedNoteLossIds.length > 0) {
    formData.append(
      "acceptedNoteLossIdsCsv",
      data.acceptedNoteLossIds.join(",")
    );
  }

  // =========================================================
  // forceApply (confirmação do usuário)
  // =========================================================
  const forceApply = !!data.forceApply;

  console.log("textService - FormData para update montado:");
  for (let pair of formData.entries()) {
    console.log(pair[0] + ": " + pair[1]);
  }

  return api.put(`/texts/${data.textId}`, formData, {
    params: { useElastic, forceApply },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function deleteText(id, useElastic = false) {
  return api.delete(`/texts/${id}`, {
    params: { useElastic },
  });
}

export function deleteTextsBatch(ids, useElastic = false) {
  //alert('ids:' + ids)
  return api.delete("/texts", {
    params: { useElastic }, // vai na query string
    data: ids, // vai no corpo da requisição
  });
}

// textService.js
export async function fetchTextsPaged(
  pageNumber,
  pageSize,
  search,
  searchFields,
  myTextsOnly, // 🔥 NOVO
  filters = {}, // 🔍 NOVO: filtros por campo (title, authorName, createdFrom…)
) {
  const params = new URLSearchParams();

  params.append("pageNumber", pageNumber);
  params.append("pageSize", pageSize);
  params.append("search", search ?? "");

  (searchFields || []).forEach((f) => params.append("searchFields", f));

  // 🔥 FLAG EXTRA (booleano explícito)
  params.append("myTextsOnly", myTextsOnly ? "true" : "false");

  // 🔍 Filtros por campo — só os preenchidos vão na query string
  Object.entries(filters || {}).forEach(([key, value]) => {
    const v = typeof value === "string" ? value.trim() : value;
    if (v === "" || v === null || typeof v === "undefined") return;
    params.append(key, v);
  });

  return api.get(`/texts/paged?${params.toString()}`);
}

export async function getFileContent(filepath) {
  const encodedPath = encodeURI(filepath); // não codifica as barras
  // ▼▼▼ ADICIONE A CONFIGURAÇÃO DE 'responseType' ▼▼▼
  const response = await api.get(`/texts/file/${encodedPath}`, {
    responseType: "text", // <<-- ISSO DIZ AO AXIOS PARA TRATAR A RESPOSTA COMO TEXTO PURO
  });
  // ▲▲▲ FIM DA MODIFICAÇÃO ▲▲▲
  return response.data;
}

// --- NOVOS MÉTODOS PARA PERMISSÕES ---

/**
 * Adiciona uma permissão a um texto para um usuário específico.
 * @param {number} textId O ID do texto.
 * @param {object} permissionData Dados da permissão (userId, permissionId).
 */
export function addTextPermission(textId, permissionData) {
  return api.post(`/texts/${textId}/permissions`, {
    ...permissionData,
    textId,
  });
}

/**
 * Remove uma permissão específica de um texto para um usuário.
 * @param {number} textId O ID do texto.
 * @param {number} userId O ID do usuário.
 * @param {number} permissionId O ID da permissão.
 */
export function removeTextPermission(textId, userId, permissionId) {
  return api.delete(`/texts/${textId}/permissions`, {
    params: { userId, permissionId },
  });
}

/**
 * Obtém todas as permissões associadas a um texto.
 * @param {number} textId O ID do texto.
 */
export function fetchTextPermissions(textId) {
  return api.get(`/texts/${textId}/permissions`);
}

// ✅ NOVA FUNÇÃO PARA ATUALIZAR O STATUS (ATIVAR/INATIVAR)
/**
 * Atualiza o status de um texto (ativo/inativo).
 * @param {number} textId O ID do texto.
 * @param {boolean} active O novo status.
 */
export function updateTextStatus(textId, active) {
  return api.patch(`/texts/${textId}/status`, { active });
}
